import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import AssistantRoundedIcon from '@mui/icons-material/AssistantRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Container,
  Stack,
  Typography,
} from '@mui/material';

import { alpha, styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DocumentToJiraWizard from './user-story-agent/DocumentToJiraWizard';
import GenerateTestCasesAgent from './GenerateTestCasesAgent';
import GenerateTestPlanModal from '../../components/project-dashboard/test-plans/GenerateTestPlanModal';
import { useUserAccess } from '../../hooks/useRole';
import { getProjectAIAccess } from '../../modules/aiConfig';
import QUERY_KEYS from '../../query/queryKeys';
import { WarningAmber } from '@mui/icons-material';

const AGENTS = [
  {
    id: 'user-story-agent',
    title: 'User Story Agent',
    description:
      'Generate Jira stories, acceptance criteria, and test cases from documents or Confluence pages.',
    icon: AutoStoriesRoundedIcon,
    badge: 'New',
  },
  {
    id: 'generate-test-cases',
    title: 'Generate Test Cases',
    description:
      'Create AI-powered test cases from a scenario, Jira story, uploaded document, or pasted requirements.',
    icon: AutoAwesomeRoundedIcon,
    badge: 'New',
  },
  {
    id: 'defect-intelligence',
    title: 'Defect Intelligence',
    description:
      'Classify, deduplicate, and surface insights from your Jira defects using AI-powered analysis.',
    icon: BugReportRoundedIcon,
  },
  {
    id: 'test-intelligence',
    title: 'Test Intelligence',
    description:
      'Analyze your test cases and requirements to generate prioritized test plan suggestions for your next cycle.',
    icon: AssistantRoundedIcon,
  },
];

const ROUTE_AGENTS = {
  'defect-intelligence': () => '/defect-intelligence',
};

const AGENT_AI_FEATURES = {
  'user-story-agent': 'storyGeneration',
  'generate-test-cases': 'testCaseGeneration',
  'defect-intelligence': 'defectIntelligence',
};

const AgentGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(3),
}));

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
  width: 260,
  borderRadius: 16,
  border: `1px solid ${disabled
    ? theme.palette.divider
    : alpha(theme.palette.primary.main, 0.2)
    }`,
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  opacity: disabled ? 0.65 : 1,
  overflow: 'hidden',

  '&:hover': disabled
    ? {}
    : {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
      borderColor: alpha(theme.palette.primary.main, 0.35),
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
}));

const AgentCard = ({ agent, onSelect }) => {
  const { id, title, description, icon: Icon, badge, comingSoon, disabledReason } = agent;
  const isDisabled = comingSoon || !!disabledReason;

  return (
    <StyledCard disabled={isDisabled}>
      <CardActionArea
        disabled={isDisabled}
        onClick={() => !isDisabled && onSelect(id)}
        onKeyDown={(e) => {
          if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect(id);
          }
        }}
        sx={{ height: '100%', p: 3 }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ height: '100%' }}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', minHeight: 24 }}>
            {comingSoon
              ? <Chip label="Coming Soon" size="small" />
              : badge && <Chip label={badge} size="small" color="primary" />
            }
          </Box>

          <IconWrapper>
            <Icon sx={{ fontSize: 34 }} />
          </IconWrapper>

          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {description}
          </Typography>
          {disabledReason && (
            <Box
              sx={{
                width: '100%',
                mt: 'auto',
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <WarningAmber sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
              <Typography variant="caption" color="warning.dark" sx={{ lineHeight: 1.4 }}>
                {disabledReason}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardActionArea>
    </StyledCard>
  );
};


const AgenticAiPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [activeAgent, setActiveAgent] = useState(null);
  const { isFullAccess, projectIds } = useUserAccess();
  const canAccessDefectIntelligence =
    isFullAccess || projectIds.includes(projectId);

  const { data: aiAccessMap = {}, isLoading } = useQuery({
    queryKey: QUERY_KEYS.project.aiAccess(projectId),
    queryFn: async () => {
      const entries = await Promise.all(
        Object.entries(AGENT_AI_FEATURES).map(
          async ([agentId, featureKey]) => {
            const access = await getProjectAIAccess(
              projectId,
              featureKey,
            );

            return [agentId, access];
          },
        ),
      );

      return Object.fromEntries(entries);
    },
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const AGENT_COMPONENTS = useMemo(
    () => ({
      'user-story-agent': DocumentToJiraWizard,
      'generate-test-cases': GenerateTestCasesAgent,
      'test-intelligence': GenerateTestPlanModal,
    }),
    [],
  );

  const agents = useMemo(
    () =>
      AGENTS.map((agent) => {
        if (
          agent.id === 'defect-intelligence' &&
          !canAccessDefectIntelligence
        ) {
          return {
            ...agent,
            disabledReason: 'Admin only',
          };
        }

        const aiAccess = aiAccessMap[agent.id];

        if (aiAccess && !aiAccess.has_access) {
          return {
            ...agent,
            disabledReason:
              aiAccess.message ||
              'AI not available for this project',
          };
        }

        return agent;
      }),
    [canAccessDefectIntelligence, aiAccessMap],
  );

  const handleSelect = (id) => {
    const selectedAgent = agents.find((a) => a.id === id);

    if (!selectedAgent || selectedAgent.disabledReason) {
      return;
    }

    if (ROUTE_AGENTS[id]) {
      navigate(ROUTE_AGENTS[id](projectId));
    } else {
      setActiveAgent(id);
    }
  };

  const ActiveComponent = AGENT_COMPONENTS[activeAgent];

  const handleClose = () => setActiveAgent(null);

  return (
    <Container maxWidth={false} sx={{ mt: 3, pb: 4 }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
            AI Agents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Intelligent agents that automate your engineering workflow.
          </Typography>
        </Box>

        {/* Agent Cards */}
        <AgentGrid>
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onSelect={handleSelect}
            />
          ))}
        </AgentGrid>
      </Stack>

      {/* Modal Agent Wizard */}
      {ActiveComponent && !isLoading && (
        <ActiveComponent
          open
          onClose={handleClose}
          handleClose={handleClose}
          projectId={projectId}
        />
      )}
    </Container>
  );
};

export default AgenticAiPage;